import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import {
  AuthorizationRequest,
  PortalAccess,
  PortalService,
} from "@kelyn-technologies/dsio-angular-lib/dsio-api";
import { AuthorizationRequestService } from "@kelyn-technologies/dsio-angular-lib";
import { Observable, of, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";

@Component({
  selector: "app-authorizations",
  templateUrl: "./authorizations.component.html",
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  styleUrls: ["./authorizations.component.css"],
})
export class AuthorizationsComponent implements OnInit {
  portalId: string | null = null;
  requestId: string | null = null;
  authorizationRequest: AuthorizationRequest | null = null;
  isLoading = false;
  errorMessage = "";
  payloadText = "{}";
  payloadError = "";

  constructor(
    private readonly route: ActivatedRoute,
    private readonly authorizationRequestService: AuthorizationRequestService,
    private readonly portalService: PortalService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.portalId = params.get("portalId");
      this.requestId = params.get("requestId");
      this.authorizationRequest = null;
      this.errorMessage = "";
      this.payloadError = "";
      this.payloadText = "{}";

      if (!this.portalId || !this.requestId) {
        this.errorMessage =
          "Missing portal or authorization request information.";
        return;
      }

      this.loadAuthorization();
    });
  }

  refresh(): void {
    this.loadAuthorization();
  }

  submitUpdate(): void {
    if (!this.requestId) {
      this.errorMessage = "Missing authorization request ID.";
      return;
    }

    const payload = this.parsePayload();
    if (!payload) {
      return;
    }

    this.isLoading = true;
    this.authorizationRequestService
      .updateAuthorizationRequest(this.requestId, payload)
      .subscribe({
        next: () => {
          this.loadAuthorization();
        },
        error: () => {
          this.isLoading = false;
          this.showMessage(
            "Failed to submit authorization update. Please try again."
          );
        },
      });
  }

  private loadAuthorization(): void {
    if (!this.requestId || !this.portalId) {
      return;
    }

    this.isLoading = true;
    this.authorizationRequestService
      .getAuthorizationRequest(this.requestId)
      .subscribe({
        next: (authorizationRequest: AuthorizationRequest) => {
          this.authorizationRequest = authorizationRequest;
          this.isLoading = false;

          if (authorizationRequest.is_valid) {
            this.attemptPortalAccess();
          }
        },
        error: () => {
          this.isLoading = false;
          this.showMessage(
            "Failed to load authorization request. Please try again."
          );
        },
      });
  }

  private attemptPortalAccess(): void {
    if (!this.portalId) {
      return;
    }

    this.resolvePortalAccess(this.portalId).subscribe({
      next: (result) => {
        if (result.kind !== "navigate") {
          this.showMessage("Portal access still requires authorization.");
          return;
        }
        this.openPortal(result.url);
      },
      error: () => {
        this.showMessage("Failed to get portal access. Please try again.");
      },
    });
  }

  private resolvePortalAccess(
    portalId: string
  ): Observable<PortalAccessOutcome> {
    const fetchAccess = () => this.portalService.getPortalAccess(portalId);
    return fetchAccess().pipe(
      switchMap((access) => this.handlePortalAccess(portalId, access, fetchAccess))
    );
  }

  private handlePortalAccess(
    portalId: string,
    access: PortalAccess,
    fetchAccess: () => Observable<PortalAccess>
  ): Observable<PortalAccessOutcome> {
    switch (access.action) {
      case "navigate":
        return this.toNavigateResult(access);
      case "request_access": {
        const grantId = access.target?.trim();
        if (!grantId) {
          return throwError(() => new Error("Grant ID is missing for this portal."));
        }
        return this.authorizationRequestService
          .createAuthorizationRequest(grantId)
          .pipe(
            switchMap((authorizationRequest) => {
              if (authorizationRequest.is_valid) {
                return fetchAccess().pipe(
                  switchMap((nextAccess) => {
                    if (nextAccess.action !== "navigate") {
                      return throwError(
                        () =>
                          new Error("Portal access still requires authorization.")
                      );
                    }
                    return this.toNavigateResult(nextAccess);
                  })
                );
              }

              const requestId = authorizationRequest.id?.trim();
              if (!requestId) {
                return throwError(
                  () => new Error("Authorization request ID is missing.")
                );
              }
              const outcome: PortalAccessOutcome = {
                kind: "needs_authorization",
                portalId,
                requestId,
              };
              return of(outcome);
            })
          );
      }
      case "update_access": {
        const requestId = access.target?.trim();
        if (!requestId) {
          return throwError(
            () => new Error("Authorization request ID is missing.")
          );
        }
        const outcome: PortalAccessOutcome = {
          kind: "needs_authorization",
          portalId,
          requestId,
        };
        return of(outcome);
      }
      case "deny":
        return of({ kind: "deny" });
      default:
        return throwError(
          () => new Error("Unknown portal access action received.")
        );
    }
  }

  private toNavigateResult(access: PortalAccess): Observable<PortalAccessOutcome> {
    const url = this.resolvePortalUrl(access);
    if (!url) {
      return throwError(
        () => new Error("Navigate action returned without a URL.")
      );
    }
    return of({ kind: "navigate", url });
  }

  private resolvePortalUrl(access: PortalAccess): string | null {
    const targetUrl = access.target?.trim();
    const dataUrl = (access.data as { url?: string } | undefined)?.url?.trim();
    return targetUrl || dataUrl || null;
  }

  updatePayloadText(value: string): void {
    this.payloadText = value;
    this.payloadError = "";
    try {
      JSON.parse(this.payloadText);
    } catch {
      this.payloadError = "Update payload must be valid JSON.";
    }
  }

  private parsePayload(): object | null {
    this.payloadError = "";
    try {
      return JSON.parse(this.payloadText);
    } catch {
      this.payloadError = "Update payload must be valid JSON.";
      return null;
    }
  }

  private openPortal(url: string): void {
    let fullUrl = url;
    if (url.startsWith("http://")) {
      fullUrl = fullUrl.replace("http://", "https://");
    } else if (!url.startsWith("https://")) {
      fullUrl = `https://${url}`;
    }
    window.open(fullUrl, "_blank");
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, "Close", { duration: 5000 });
  }
}

type PortalAccessOutcome =
  | { kind: "navigate"; url: string }
  | { kind: "needs_authorization"; requestId: string; portalId: string }
  | { kind: "deny" };
